import { Injectable, signal } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import {
  DialogComponent,
  DialogData,
  DialogResult,
} from "../../modals/dialog/dialog.component";

@Injectable({
  providedIn: "root",
})
export class ModalService {
  private fieldSelectionUpdateSubject = new BehaviorSubject<boolean>(false);
  private fieldViewEntitySubject = new BehaviorSubject<boolean>(false);
  private fieldViewManagerSubject = new BehaviorSubject<boolean>(false);
  // Define the Subject of Help
  private docModalSubject = new BehaviorSubject<{
    visible: boolean;
    fileUrl: string;
    fileName: string;
    title: string;
  }>({
    visible: false,
    fileUrl: "",
    fileName: "",
    title: "",
  });
  // Define the Subject of Contact
  private contactModalSubject = new BehaviorSubject<boolean>(false);
  // Define the Subject of Profile
  private profileModalSubject = new BehaviorSubject<boolean>(false);
  // Email Preferences modal — signal-based
  emailPreferencesModal = signal(false);
  // Closed by default — opened on bootstrap only for first-run growers (see
  // the Welcome component's gate). Dismissing it persists `welcome_seen_at`.
  welcomeModal = signal(false);
  private dialogHost!: DialogComponent;

  fieldSelectUpdateModal$ = this.fieldSelectionUpdateSubject.asObservable();
  fieldViewEntitySubject$ = this.fieldViewEntitySubject.asObservable();
  fieldViewManagerSubject$ = this.fieldViewManagerSubject.asObservable();
  // Define the doc Modal
  docModal$ = this.docModalSubject.asObservable();
  // Define the Contat Modal
  contactModal$ = this.contactModalSubject.asObservable();
  // Define the Profile Modal
  profileModal$ = this.profileModalSubject.asObservable();

  constructor() {}

  registerDialog(host: DialogComponent) {
    this.dialogHost = host;
  }

  /**
   * ! Warning: Dialog message is rendered as innerHTML, be cautious of XSS attacks.
   */
  openDialogAsync = (data: DialogData): Promise<DialogResult> => {
    if (!this.dialogHost) {
      console.error("Dialog host is not registered.");
      return Promise.reject("DIALOG_HOST_NOT_REGISTERED");
    }
    return new Promise((resolve, reject) => {
      // If no actions provided, add default OK action
      const actionsToUse =
        data.actions && data.actions.length > 0 ?
          data.actions
        : [
            {
              label: "OK",
              action: () => "confirm" as DialogResult,
            },
          ];

      const wrappedAction = actionsToUse.map((actionObj) => ({
        ...actionObj,
        action: () => {
          this.closeDialog();
          try {
            const res = actionObj.action();
            resolve(res);
            return res;
          } catch (error) {
            console.error("Error executing dialog action:", error);
            reject(error);
            throw error;
          }
        },
      }));
      this.dialogHost.dialogData = {
        ...data,
        actions: wrappedAction,
      };
      this.dialogHost.isOpen = true;
    });
  };

  closeDialog() {
    if (this.dialogHost) {
      this.dialogHost.isOpen = false;
    }
  }

  toggleFieldSelectionUpdateModal(): void {
    this.fieldSelectionUpdateSubject.next(
      !this.fieldSelectionUpdateSubject.value,
    );
  }

  // Method to turn the modal ON
  turnFieldSelectionUpdateModalOn(): void {
    this.fieldSelectionUpdateSubject.next(true); // Set the value to true (modal open)
  }

  // Method to turn the modal OFF
  turnFieldSelectionUpdateModalOff(): void {
    this.fieldSelectionUpdateSubject.next(false); // Set the value to false (modal closed)
  }

  toggleFieldViewEntityModal(): void {
    this.fieldViewEntitySubject.next(!this.fieldViewEntitySubject.value);
  }

  toggleFieldViewManagerModal(): void {
    this.fieldViewManagerSubject.next(!this.fieldViewManagerSubject.value);
  }

  openDocModal(
    fileUrl: string,
    fileName: string,
    title: string = "Documentation",
  ): void {
    this.docModalSubject.next({
      visible: true,
      fileUrl,
      fileName,
      title,
    });
  }

  closeDocModal(): void {
    this.docModalSubject.next({
      visible: false,
      fileUrl: "",
      fileName: "",
      title: "",
    });
  }

  // Backward-compatible wrapper for existing callers.
  toggleDocModal(
    fileUrl: string,
    fileName: string,
    title: string = "Documentation",
  ): void {
    const current = this.docModalSubject.value;
    if (current.visible) {
      this.closeDocModal();
      return;
    }

    this.openDocModal(fileUrl, fileName, title);
  }

  // Function of toggle contactModal
  toggleContactModal(): void {
    this.contactModalSubject.next(!this.contactModalSubject.value);
  }

  // Function of toggle profileModal
  toggleProfileModal(): void {
    this.profileModalSubject.next(!this.profileModalSubject.value);
  }
}
